import { useMemo, useState, Fragment } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Stack,
  Pagination,
} from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";

import { useDispatch, useSelector } from "react-redux";
import ValidationStatusSkeleton from "../../BrandSkeletons/ValidationPopupSkeleton";
import { BrandConfigReportEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import SaveIcon from "@mui/icons-material/Save";

export type ValidationStatus =
  | "Passed"
  | "Warning"
  | "Failed"
  | "Running"
  | "Pending";

export interface ValidationItem {
  step: string;
  status: ValidationStatus;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  brandId: string | number | null;
}

type ValidationStatusKey =
  | "passed"
  | "warning"
  | "failed"
  | "running"
  | "pending";

const STATUS_CONFIG: Record<
  ValidationStatusKey,
  {
    label: string;
    color: string;
    bg: string;
    border: string;
    icon: JSX.Element;
  }
> = {
  passed: {
    label: "Passed",
    color: "#1E4620",
    bg: "#EDF7ED",
    border: "#2E7D3280",
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
  },
  failed: {
    label: "Failed",
    color: "#5F2120",
    bg: "#FDEDED",
    border: "#D32F2F80",
    icon: <CancelOutlinedIcon fontSize="small" />,
  },
  warning: {
    label: "Warning",
    color: "#663C00",
    bg: "#FFF4E5",
    border: "#ED6C0280",
    icon: <WarningAmberOutlinedIcon fontSize="small" />,
  },
  running: {
    label: "Running",
    color: "#7B1FA2",
    bg: "#9C27B01F",
    border: "#9C27B080",
    icon: <AutorenewIcon fontSize="small" />,
  },
  pending: {
    label: "Pending",
    color: "#014361",
    bg: "#E5F6FD",
    border: "#2196F380",
    icon: <PendingOutlinedIcon fontSize="small" />,
  },
};

function StatusChip({ status }: { status?: string }) {
  const key = status?.toLowerCase();

  if (!key || !(key in STATUS_CONFIG)) {
    return null;
  }

  const config = STATUS_CONFIG[key as ValidationStatusKey];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 0.5,
        py: 0.3,
        borderRadius: "4px",
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        width: "fit-content",
      }}
    >
      <span style={{ display: "flex", color: config.color }}>
        {config.icon}
      </span>
      <span style={{ fontSize: "13px", fontWeight: 500, color: config.color }}>
        {config.label}
      </span>
    </Stack>
  );
}

// keys never rendered as columns (used only for baseline ordering)
const HIDDEN_KEYS = new Set(["TestNumber"]);

export default function ValidationStatusDialog({
  open,
  onCancel,
  brandId,
}: Props) {
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "asc" });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const oauthTokenHeader = useSelector(selectUserToken) as any;

  const testAgainPopupdata = useSelector(
    (state: any) => state.brandConfigurationReport.testAgainPopupdata,
  );

  const testAgainPopupMetadata = useSelector(
    (state: any) => state.brandConfigurationReport.testAgainPopupMetadata,
  );

  const isFetchedPopupData = useSelector(
    (state: any) => state.brandConfigurationReport.isFetchedPopupData,
  );

  // UI label overrides as per Figma designs, for specific metadata keys
  const LABEL_OVERRIDES: Record<string, string> = {
    Test: "Validation Type", // from API get response "Validation Name"
  };

  // columns built from metadata, ordered by `order`, TestNumber excluded
  const columns = useMemo(() => {
    return Object.entries(testAgainPopupMetadata || {})
      .filter(([key]) => !HIDDEN_KEYS.has(key))
      .sort(([, a]: any, [, b]: any) => Number(a.order) - Number(b.order))
      .map(([key, meta]: [string, any]) => ({
        key,
        label: LABEL_OVERRIDES[key] ?? meta.title,
        sortable: true,
      }));

    // eslint-disable-next-line
  }, [testAgainPopupMetadata]);

  const sortedData = useMemo(() => {
    const rows = [...(testAgainPopupdata || [])];

    // baseline: always order by TestNumber ascending (numeric)
    rows.sort((a, b) => {
      const an = Number(a?.TestNumber);
      const bn = Number(b?.TestNumber);
      if (isNaN(an) && isNaN(bn)) return 0;
      if (isNaN(an)) return 1;
      if (isNaN(bn)) return -1;
      return an - bn;
    });

    if (!sort.keytoSort) return rows;

    rows.sort((a, b) => {
      const aValue = a[sort.keytoSort] ?? "";
      const bValue = b[sort.keytoSort] ?? "";
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      const bothNumeric =
        aValue !== "" && bValue !== "" && !isNaN(aNum) && !isNaN(bNum);
      const compare = bothNumeric
        ? aNum - bNum
        : String(aValue).localeCompare(String(bValue));
      return sort.direction === "asc" ? compare : -compare;
    });

    return rows;
  }, [testAgainPopupdata, sort]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (col: any) => {
    if (!col.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: col.key,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        col.key === sort.keytoSort && sort.direction === "asc" ? "desc" : "asc",
    });
  };

  //-- Function to handle save to table
  const handleSaveToTable = async () => {
    if (!brandId || isSaving) return;

    setIsSaving(true);
    try {
      const data = {};
      const response = await BrandConfigReportEndPointLogic.manuallyApprove(
        oauthTokenHeader,
        data,
        brandId,
      );

      // success only when we got the response object back with status 200
      if (response?.status === 200) {
        onCancel(); // closes + resets brandId in parent
        dispatch(
          setDisplayErrorSnackBar({
            message: "Approved the validation.",
            type: "success",
          }),
        );
        return;
      }

      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to approve the validation. Please try again.",
          type: "error",
        }),
      );
    } catch (e: any) {
      dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Something went wrong. Please try again.",
          type: "error",
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          isSaving &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        onCancel();
      }}
      maxWidth="lg"
      fullWidth
    >
      {isFetchedPopupData ? (
        <>
          <DialogTitle sx={{ fontWeight: 600, fontSize: "20px" }}>
            Validation Status
          </DialogTitle>

          <DialogContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.key} align="left">
                        <TableSortLabel
                          active={sort.keytoSort === col.key}
                          direction={
                            sort.keytoSort === col.key ? sort.direction : "asc"
                          }
                          onClick={() => handleHeaderClick(col)}
                          disabled={!col.sortable}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows?.length > 0 ? (
                    paginatedRows.map((row: any, rowIndex: number) => (
                      <Fragment key={rowIndex}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {columns.map((col) => (
                            <TableCell key={col.key} align="left">
                              {col.key === "Status" ? (
                                row[col.key] ? (
                                  <StatusChip status={row.Status} />
                                ) : (
                                  ""
                                )
                              ) : (
                                (row[col.key] ?? "")
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        No Data Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {sortedData?.length > 0 && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <TablePagination
                  rowsPerPageOptions={[]}
                  component="div"
                  count={sortedData?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                      { display: "none" },
                    ".MuiTablePagination-actions": { display: "none" },
                  }}
                />
                <Pagination
                  count={Math.ceil(sortedData?.length / rowsPerPage)}
                  page={page + 1}
                  onChange={(_: any, value: any) =>
                    handleChangePage(null, value - 1)
                  }
                  showFirstButton
                  showLastButton
                  className="pagination-button"
                  color="secondary"
                  size="small"
                  sx={{
                    ".css-wjh20t-MuiPagination-ul": {
                      justifyContent: "center",
                    },
                  }}
                />
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={sortedData?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  classes={{ displayedRows: "hidden-displayed-rows" }}
                  sx={{
                    ".MuiTablePagination-actions": { display: "none" },
                    ".MuiTablePagination-input": { marginRight: "15px" },
                  }}
                />
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={onCancel}
              sx={{ color: "#7B1FA2", fontWeight: 500 }}
            >
              CANCEL
            </Button>
            <Button
              onClick={() => handleSaveToTable()}
              variant="contained"
              loading={isSaving}
              loadingPosition="end"
              endIcon={isSaving && <SaveIcon />}
              disabled={isSaving}
              sx={{
                backgroundColor: "#7B1FA2",
                fontWeight: 500,
                "&:hover": { backgroundColor: "#6A1B9A" },
              }}
            >
              APPROVE
            </Button>
          </DialogActions>
        </>
      ) : (
        <ValidationStatusSkeleton onCancel={onCancel} />
      )}
    </Dialog>
  );
}
